import { config } from '@app/config';
import { create as _create, StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { devtools, persist, PersistOptions } from 'zustand/middleware';
import { PersistStoreApi, registerPersistStore } from './persistence-registry';

/** Persist options for createAppStore. purgeOnLogout (default true) controls whether the store is cleared on logout. */
export interface CreateAppStorePersistOptions<T, U = T> extends PersistOptions<T, U> {
  /** When false, the store is persisted but not cleared on logout. Default true. */
  purgeOnLogout?: boolean;
}

/** Options for createAppStore. When persist is set, the store is persisted and registered for purge on logout (unless purgeOnLogout: false). */
export interface CreateAppStoreOptions<T, U = T> {
  persist: CreateAppStorePersistOptions<T, U>;
}

/**
 * Conditional store creator that wraps with devtools in development only.
 * Supports an optional second parameter with persist options; when provided,
 * the initializer is wrapped with persist and the store is registered for purge (clearStorage) and PersistGate.
 *
 * @param name - Optional name for the store (used in devtools)
 * @param options - Optional. Pass { persist: PersistOptions } to persist the store and register for purge.
 * @returns A function that accepts the state creator (or persist state creator when persist is used)
 */
export function createAppStore<T>(
  name?: string,
): (initializer: StateCreator<T>) => UseBoundStore<StoreApi<T>>;

export function createAppStore<T, U = T>(
  name: string | undefined,
  options: CreateAppStoreOptions<T, U>,
): (initializer: StateCreator<T, [], [['zustand/persist', U]]>) => UseBoundStore<StoreApi<T>>;

export function createAppStore<T, U = T>(
  name?: string,
  options?: CreateAppStoreOptions<T, U>,
): (
  initializer: StateCreator<T, [], []> | StateCreator<T, [], [['zustand/persist', U]]>,
) => UseBoundStore<StoreApi<T>> {
  return (initializer) => {
    let middlewareResult: StateCreator<T, [], []>;
    let purgeOnLogout: boolean | undefined;

    if (options?.persist) {
      const { purgeOnLogout: purgeFlag, ...persistOptionsForZustand } = options.persist;
      purgeOnLogout = purgeFlag;
      middlewareResult = persist(
        initializer as StateCreator<T, [], [['zustand/persist', U]]>,
        persistOptionsForZustand as PersistOptions<T, U>,
      ) as StateCreator<T, [], []>;
    } else {
      middlewareResult = initializer as StateCreator<T, [], []>;
    }

    let store: UseBoundStore<StoreApi<T>>;
    const stateCreator = middlewareResult as Parameters<ReturnType<typeof _create<T>>>[0];
    if (config.isNodeProd) {
      store = _create<T>()(stateCreator);
    } else {
      store = _create<T>()(devtools(stateCreator, { name: name ?? undefined, enabled: true }));
    }

    const storeWithPersist = store as UseBoundStore<StoreApi<T>> & { persist?: unknown };
    if (options?.persist && storeWithPersist.persist) {
      registerPersistStore(storeWithPersist as PersistStoreApi, {
        purgeOnLogout: purgeOnLogout ?? true,
      });
    }
    return store;
  };
}
